/**
 * GROQ projection builder for the Sanity adapter.
 *
 * The page document holds its blocks in one or more named array fields ("slots")
 * — e.g. Eneco's schema uses `pageBuilder`. Each slot is projected as an array
 * of blocks; the adapter turns each named array into a `CmsRendering`
 * placeholder.
 *
 * References are inlined here (the eager-hydration contract) via per-`_type`
 * conditional projection overrides — see `blockProjections` below — because
 * different block types reference different things (a `hero`'s button links
 * vs a `cardGrid`'s article references). Blocks with no override just spread
 * `...` shallowly.
 *
 * Every reference the projection leaves, under any field name, is filled in
 * afterwards by `references.ts`, with {@link buildReferencedDocumentsQuery}.
 */

/**
 * Default slot fields treated as named placeholders — every one is projected, and whichever the
 * document actually has wins downstream (`CmsLayout`'s `MAIN_SLOT_CANDIDATES`).
 *
 * `sections` is first because it is the field every generated page document type declares
 * (`libs/cms-sanity-schema/src/documents/*.ts`), i.e. the one a migrated dataset actually uses.
 * It was previously absent, so a migrated page came back with no slots at all unless
 * `SANITY_SLOT_FIELDS` was set — the query selected four fields that did not exist, `CmsLayout`
 * logged `cMsLy1`, and the page rendered `null`. `pageBuilder` is the greenfield POC schema (still
 * live in dev); `main`/`footer`/`header` are the playbook convention and the test fixtures.
 *
 * Override via `createSanityAdapter({ slotFields })` / `SANITY_SLOT_FIELDS`.
 */
export const PLACEHOLDER_FIELDS = ['sections', 'pageBuilder', 'main', 'footer', 'header'] as const;

/**
 * Reference on the page document to its chrome (`pageChrome`), whose own `sections` array holds the
 * header/footer blocks — `MainNavigation`/`Footer` at the root, `MegaMenuV2`/`SubFooterMenu` beneath
 * them. Chrome used to sit inline in the page's own `sections`; a later migration run lifted it out into
 * a shared document, so the dataset now holds both shapes at once (see `adapter.ts#buildTree`).
 *
 * Deliberately not an entry in {@link PLACEHOLDER_FIELDS}: those name arrays on the page document
 * itself, and this is a dereference. Aliased to {@link CHROME_SLOT_FIELD} so it lands at the top level
 * anyway and pools like any other slot — the blocks inside carry the same `area`/`parentKey` bookkeeping
 * as page blocks, so they need no other special handling.
 */
const CHROME_REFERENCE_FIELD = 'chrome';

/** Top-level key the chrome document's blocks are projected onto. See {@link CHROME_REFERENCE_FIELD}. */
export const CHROME_SLOT_FIELD = 'chromeSections';

/**
 * Resolves a `link` object's `internalLink` reference to `{ _type, slug }` so
 * `mappers.ts#toLink` never needs to dereference anything itself. Reused by
 * every block whose buttons carry a `link` field.
 */
const LINK_REFERENCE_PROJECTION = `"buttons": buttons[]{
    ...,
    "link": link{ ..., "internalLink": internalLink->{ _type, "slug": slug.current } }
  }`;

/**
 * The menu documents (`navigationMenu`) hold their entries in `items[]`, each a `navItem` whose `link` is
 * the v3 `{ linkType, reference, text }` shape. Those are dereferenced here for the same reason
 * {@link LINK_FIELDS} exists: `mappers.ts#toLink` resolves no references itself, so an item left as a
 * bare `_ref` renders as a link to nowhere.
 *
 * Two levels deep, because a menu entry is either a `navItem` or a `navGroup` that holds `navItem`s of
 * its own. Projecting only the outer level left every grouped link unresolved — the group's heading
 * rendered with empty rows beneath it. GROQ has no recursive projection, so the nesting is written out;
 * real content goes no deeper.
 */
const NAV_ITEM_PROJECTION = `..., "link": link{ ..., "reference": reference->{ _type, "slug": slug.current, browserTitle } }`;

/**
 * Default per-`_type` projection overrides. `hero`/`ctaBlock`/`cardGrid` are the greenfield POC
 * schema's types and never match a migrated block (whose types are PascalCase) — harmless, since a
 * conditional that matches nothing costs nothing.
 * `featureGrid`/`testimonials`/`faqAccordion` need no override — their fields are already flat, or
 * (for `faqAccordion`'s Portable Text) handled by `mappers.ts#toRichText` without GROQ resolution.
 *
 * `component-map.json` v3 links and shared site-wide content are handled separately, by
 * {@link LINK_FIELDS} and {@link SITE_WIDE_CONTENT_FIELD}: projected on every block by name rather than
 * per `_type`, since the same field names cover every section type.
 *
 * Override via `createSanityAdapter({ blockProjections })`.
 */
export const DEFAULT_BLOCK_PROJECTIONS: Record<string, string> = {
  hero: LINK_REFERENCE_PROJECTION,
  ctaBlock: LINK_REFERENCE_PROJECTION,
  cardGrid: `"references": references[]->{ _id, _type, title, "slug": slug.current }`,
};

/**
 * Link fields carried by the blocks this lane renders. Projected on every block regardless of
 * `_type`, so an internal link's `reference` is dereferenced to a slug before `mappers.ts#toLink`
 * sees it — the eager-hydration contract. A block without the field projects `null`, which
 * `adapter.ts#toRendering` drops.
 *
 * A name list rather than a per-`_type` list, where a per-type mapping would need an entry for each
 * section type. It is not every link: the schema bundle has many more link field names. An internal
 * link under any other name gets its page from `references.ts`, but as the page's own fields, where
 * `toLink` reads the `{ slug }` projected here, so it still renders no link. Building a link's address
 * from its page is what closes that.
 */
const LINK_FIELDS = [
  'buttonLink',
  'link',
  'footerLink',
  'secondaryButtonLink',
  'helpLink',
  'failedUrl',
  'successUrl',
] as const;

/**
 * `browserTitle` comes along on every dereferenced target so `mappers.ts#toLink` can label a link the
 * migration left without `text` — see `referenceLabel` there for why that is a fallback, not a rule.
 */

const linkProjections = LINK_FIELDS.map(
  field => `"${field}": ${field}{ ..., "reference": reference->{ _type, "slug": slug.current, browserTitle } }`,
).join(',\n    ');

/**
 * The field through which a block shows content shared across a site (ADR-0063). The schema bundle
 * gives it, under this one name, to every section whose Sitecore datasource can live under the site's
 * Global / Configuration / Library root, and sets it *instead of* the block's own fields: the document
 * it references holds the content, which `adapter.ts` offers as the block's own. `queries.spec.ts`
 * checks the name against `component-map.json`, so a bundle that names it differently fails there
 * rather than on a page.
 *
 * Projected on every block by name, like {@link LINK_FIELDS}, rather than per `_type`, so a section
 * that gains the field in a later bundle needs no change here. A block without it projects `null`,
 * which `adapter.ts#toRendering` drops. Inside it, links under the {@link LINK_FIELDS} names and the
 * menu entries are dereferenced, for the reason they are on a block: `mappers.ts#toLink` resolves no
 * references itself. Any other reference inside it is filled in by `references.ts`, as on a block.
 */
export const SITE_WIDE_CONTENT_FIELD = 'siteWideContent';

const SITE_WIDE_CONTENT_PROJECTION = `"${SITE_WIDE_CONTENT_FIELD}": ${SITE_WIDE_CONTENT_FIELD}->{
    ...,
    ${linkProjections},
    "items": items[]{
      ${NAV_ITEM_PROJECTION},
      "items": items[]{ ${NAV_ITEM_PROJECTION} }
    }
  }`;

export interface PageQueryConfig {
  /** Document field holding the language code (Document Internationalization: `language`). */
  languageField: string;
  /** Array fields on the page document to project as named placeholder slots. */
  slotFields: readonly string[];
  /** Per-`_type` GROQ projection overrides (reference/asset resolution). */
  blockProjections: Record<string, string>;
  /** Emit `_type in $documentTypes`. See {@link buildPageQuery} for when to prefer `matchSite`. */
  matchDocumentTypes?: boolean;
  /** Emit `site._ref == $site` — the durable way to scope a container to its own pages. */
  matchSite?: boolean;
}

/**
 * Build the page projection. `documentTypes`, `slugCandidates`, `language`, `locale` and `site` are
 * passed as GROQ params; field names (`languageField`, slots, projection fragments) are trusted
 * config interpolated into the query — never user input.
 *
 * **Two ways to scope a page lookup, and `site` is the better one.**
 *
 * `_type in $documentTypes` was the original, because a migrated dataset has one page document type
 * per Sitecore page template rather than a single `page` type. That list is now 189 types long, has
 * to be hand-maintained per container in an env var nothing validates, and goes stale on every
 * bundle regeneration — it has silently swallowed a whole page three separate times (an
 * unreachable `['page']` default, then a missing `enecoCoUkHomePage`, each presenting as a blank
 * page rather than an error).
 *
 * `site._ref == $site` avoids all of that: every page document carries a `site` reference
 * (ADR-0052), verified 204 of 204 in the `development` dataset across four sites, and a container
 * maps to exactly one. New page types are picked up with no config change. Prefer it; keep the type
 * list only for the greenfield POC dataset, whose documents have no `site` at all.
 *
 * Both clauses are optional and compose: with neither, the filter is slug plus language alone,
 * which is only safe on a single-site dataset.
 */
/**
 * The slot projection both page builders share.
 *
 * Extracted so the legacy full-path lookup and the ADR-0077 address lookup cannot drift in what
 * they return — only in how they find the document.
 */
const buildSlotProjection = ({
  slotFields,
  blockProjections,
}: Pick<PageQueryConfig, 'slotFields' | 'blockProjections'>): string => {
  const overrides = Object.entries(blockProjections)
    .map(([type, fragment]) => `_type == "${type}" => { ${fragment} }`)
    .join(',\n    ');
  const overridesFragment = overrides ? `,\n    ${overrides}` : '';
  const blockBody = `_key, _type, ...,\n    ${linkProjections},\n    ${SITE_WIDE_CONTENT_PROJECTION}${overridesFragment}`;
  return [
    ...slotFields.map(field => `  "${field}": ${field}[]{\n    ${blockBody}\n  }`),
    `  "${CHROME_SLOT_FIELD}": ${CHROME_REFERENCE_FIELD}->sections[]{\n    ${blockBody}\n  }`,
  ].join(',\n');
};

export const buildPageQuery = ({
  languageField,
  slotFields,
  blockProjections,
  matchDocumentTypes = true,
  matchSite = false,
}: PageQueryConfig): string => {
  const slots = buildSlotProjection({ slotFields, blockProjections });
  const filters = [
    ...(matchDocumentTypes ? ['_type in $documentTypes'] : []),
    ...(matchSite ? ['site._ref == $site'] : []),
    'slug.current in $slugCandidates',
    `${languageField} in $languageCandidates`,
  ];
  // The document's own fields are spread first, so the page's SEO values (`browserTitle`, `seo`,
  // `canonicalUrl`, `doNotIndex`, …) reach `root.content`. Without it the projection returned only
  // `_id`/`_type`/`_context` plus the slots, leaving every page-level field unfetched. The explicit
  // slot projections follow, and later attributes win, so they still override the raw arrays.
  return `*[${filters.join(' && ')}][0]{
  ...,
  _id,
  _type,
  "_context": { "locale": $locale, "pagePath": slug.current, "pageEditing": false },
${slots}
}`;
};

/**
 * The site home's reserved slug — a sentinel meaning "this is the root", never a path step.
 *
 * Mirrors `HOME_PAGE_SLUG` in the Studio's own `page-url.ts`. ADR-0077 kept ADR-0054's reserved
 * `"/"` value while narrowing its job: it no longer holds a path, it terminates a chain.
 */
export const HOME_PAGE_SLUG = '/';

/** GROQ param name for the nth ancestor segment, nearest first (`$seg0` is the page's own slug). */
const segmentParam = (index: number): string => `$seg${index}`;

/** `parentPage->parentPage->…`, `hops` deep. Zero hops is the document itself. */
const ancestorPrefix = (hops: number): string => 'parentPage->'.repeat(hops);

/**
 * Filters that pin a page to one exact address.
 *
 * ADR-0077 made an address a composition: a page stores one segment in `slug.current` plus a
 * `parentPage` reference, so `/mkb/klantenservice/jaarnota/` is three documents chained together.
 * GROQ has no recursive dereference — every join resolves exactly one level — so an n-deep address
 * is n joins, written out. We build them to the depth the *request* needs rather than to a fixed
 * ceiling, which costs nothing for a shallow path and has no maximum depth to go stale.
 *
 * Matching the whole chain in GROQ rather than fetching candidates and filtering here is what keeps
 * this one round trip and one page payload: `slug.current` is unique only per site, language *and
 * parent*, so `jaarnota` alone can name several documents, and only the chain tells them apart.
 *
 * The final clause is the load-bearing one. Terminating on the home sentinel is what makes a
 * partial match fail rather than resolve to the wrong page — without it, a request for
 * `/klantenservice/jaarnota/` would happily match a `jaarnota` sitting under a `klantenservice`
 * somewhere deeper in the tree.
 */
const addressFilters = (segmentCount: number): string[] => {
  // The home page: its slug *is* the sentinel, and it has no ancestors to check.
  if (segmentCount === 0) return [`slug.current == $homeSlug`];

  return [
    // Segments are passed nearest-first, so `$seg0` is the last segment of the URL.
    ...Array.from({ length: segmentCount }, (_, hop) => `${ancestorPrefix(hop)}slug.current == ${segmentParam(hop)}`),
    // One hop past the shallowest segment must be the site home, or this is a different page.
    `${ancestorPrefix(segmentCount)}slug.current == $homeSlug`,
  ];
};

/**
 * `frontEndRootPath` for the page and each of its ancestors, nearest first.
 *
 * A flow's pages share a base path taken from the flow root — the nearest ancestor (or the page
 * itself) flagged `frontEndRootPath`. Reading the flag off the chain we already resolved replaces
 * guessing it from the URL, which could only ever handle a two-segment flow.
 */
const rootFlagProjection = (segmentCount: number): string =>
  // From hop 1: the page's own flag is `frontEndRootPath`, which the projection's `...` returns
  // already. An empty string for a home-page lookup, which has no ancestors to ask about.
  Array.from(
    { length: segmentCount },
    (_, index) => `"_root${index + 1}": ${ancestorPrefix(index + 1)}frontEndRootPath`,
  ).join(',\n  ');

/**
 * `flowName` for each of the page's ancestors, nearest first, beside the flags above.
 *
 * The migration stores a flow's name on its root only, where Sitecore sends it on every page of the
 * flow. A flow keys its visitor's answers by that name, so a step takes it from its root.
 */
const rootNameProjection = (segmentCount: number): string =>
  Array.from(
    { length: segmentCount },
    (_, index) => `"_flowName${index + 1}": ${ancestorPrefix(index + 1)}flowName`,
  ).join(',\n  ');

/**
 * Build the page projection for one exact address — the ADR-0077 lookup.
 *
 * Same projection as {@link buildPageQuery}; only the filter differs. Kept as a sibling rather than
 * a flag on that function because the two disagree about what a slug *is*, which is the whole point
 * of the model change, and a single builder switching on it reads as if they were variants of one
 * idea rather than one superseding the other.
 */
export const buildAddressPageQuery = ({
  languageField,
  slotFields,
  blockProjections,
  matchDocumentTypes = true,
  matchSite = false,
  segmentCount,
}: PageQueryConfig & { segmentCount: number }): string => {
  const filters = [
    ...(matchDocumentTypes ? ['_type in $documentTypes'] : []),
    ...(matchSite ? ['site._ref == $site'] : []),
    ...addressFilters(segmentCount),
    `${languageField} in $languageCandidates`,
  ];

  // Empty for a home-page lookup — a bare `,` here is a GROQ syntax error, not an empty projection.
  const rootFlags = rootFlagProjection(segmentCount);
  const rootNames = rootNameProjection(segmentCount);

  return `*[${filters.join(' && ')}][0]{
  ...,
  _id,
  _type,
${rootFlags ? `  ${rootFlags},\n  ${rootNames},\n` : ''}  "_context": { "locale": $locale, "pagePath": $pagePath, "pageEditing": false },
${buildSlotProjection({ slotFields, blockProjections })}
}`;
};

/**
 * Set by {@link buildReferencedDocumentsQuery} on a fetched document that is a page, and dropped again
 * by `references.ts`, the one place that reads it.
 */
export const PAGE_MARKER = '_page';

/**
 * Fields only a page has: in the schema bundle the page types declare all three and no other document
 * type declares any (`libs/cms-sanity-schema/src/documents/*.ts`). They tell a page that has no blocks
 * stored apart from content, which its slot fields alone cannot: `sections` is optional, so a new page
 * has none until an editor adds a block.
 */
const PAGE_ONLY_FIELDS = ['slug', 'parentPage', CHROME_REFERENCE_FIELD] as const;

/**
 * The documents behind a set of references (`$ids`), for `references.ts` to fill in where the
 * references stand.
 *
 * A page comes back without its blocks. A document that carries the page's slot fields, or a field only
 * a page has ({@link PAGE_ONLY_FIELDS}), is a page: in the schema bundle the documents declaring
 * `sections` are the page types and the shared chrome (`pageChrome`). Its blocks are its own content,
 * never the content of a block that points at it, and leaving them out keeps an article list from
 * fetching every article's whole page. `defined()` reads the stored document, not the projection, so
 * the mark holds although the same projection empties those fields.
 */
export const buildReferencedDocumentsQuery = (slotFields: readonly string[]): string => {
  if (slotFields.length === 0) return '*[_id in $ids]';
  const isPage = [...slotFields, ...PAGE_ONLY_FIELDS].map(field => `defined(${field})`).join(' || ');
  const withoutBlocks = slotFields.map(field => `"${field}": null`).join(', ');
  return `*[_id in $ids]{
  ...,
  ${isPage} => { ${withoutBlocks}, "${PAGE_MARKER}": true }
}`;
};
